import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Phone, Globe, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Impressum = () => {
  const { lang } = useLanguage();
  return (
    <div className="min-h-screen bg-[#0A1628]" data-testid="impressum-page">
      {/* Header */}
      <header className="w-full px-6 py-4 border-b border-[#1A2B42]">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_kreation-hub/artifacts/haikxkku_xchannelsai_full_name_logo_transparent_colour.png" 
              alt="xchannels.ai" 
              className="h-10 sm:h-12"
            />
          </Link>
          <Link 
            to="/login" 
            className="text-[#00D9C8] hover:text-[#00D9C8]/80 flex items-center gap-2 text-sm"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-4 h-4" />
            {lang === 'de' ? 'Zurück' : 'Back'}
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-8">
            Impressum
          </h1>

          {/* Angaben gemäß § 5 TMG */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">
              Angaben gemäß § 5 TMG
            </h2>
            
            <div className="bg-[#1A2B42] rounded-xl p-6 space-y-4">
              <div>
                <h3 className="text-[#00D9C8] font-medium mb-2">Anbieter</h3>
                <p className="text-[#A0AEC0]">xchannels i. Gr.</p>
              </div>
              
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#5A6C7D] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[#A0AEC0]">c/o INiTS, Maria-Jacobi-Gasse 1</p>
                  <p className="text-[#A0AEC0]">1030 Wien, Österreich</p>
                </div>
              </div>
            </div>
          </section>

          {/* Kontakt */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">
              Kontakt
            </h2>
            
            <div className="bg-[#1A2B42] rounded-xl p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#5A6C7D]" />
                <a href="tel:+4319297964" className="text-[#A0AEC0] hover:text-[#00D9C8]">
                  +43 1 9297964
                </a>
              </div>
              
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#5A6C7D]" />
                <a href="mailto:hello@xchannels.ai" className="text-[#A0AEC0] hover:text-[#00D9C8]">
                  hello@xchannels.ai
                </a>
              </div>
              
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-[#5A6C7D]" />
                <a href="https://xchannels.ai" target="_blank" rel="noopener noreferrer" className="text-[#A0AEC0] hover:text-[#00D9C8]">
                  https://xchannels.ai
                </a>
              </div>
            </div>
          </section>

          {/* Mediengesetz */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">
              Angaben gemäß § 25 Abs 2 Mediengesetz
            </h2>
            <div className="bg-[#1A2B42] rounded-xl p-6">
              <p className="text-[#A0AEC0]">
                Gegenstand des Unternehmens: Datenverarbeitung, Consulting
              </p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">
              Angaben gemäß § 25 Abs 4 Mediengesetz
            </h2>
            <div className="bg-[#1A2B42] rounded-xl p-6">
              <p className="text-[#A0AEC0]">
                Diese Website richtet sich an Kunden und andere an IT-Themen interessierte Personen 
                und dient der Vorstellung von xchannels.ai.
              </p>
            </div>
          </section>

          {/* Haftungsausschluss */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-white mb-4">
              Haftungsausschluss
            </h2>
            
            <div className="space-y-6">
              <div className="bg-[#1A2B42] rounded-xl p-6">
                <h3 className="text-[#00D9C8] font-medium mb-3">Haftung für Inhalte</h3>
                <p className="text-[#A0AEC0] leading-relaxed">
                  Die Website xchannels.ai und die bereitgestellten Informationen wurden ausschließlich 
                  zu allgemeinen Informationszwecken sorgfältig zusammengestellt. Sie sind jedoch nicht 
                  als Beratungshilfe im Einzelfall gedacht und können auch nicht dienen. Wir übernehmen 
                  daher keine Haftung für die Richtigkeit, Aktualität und Genauigkeit der bereitgestellten 
                  Informationen. Darüber hinaus übernimmt xchannels.ai keine Haftung für Links zu externen 
                  Websites, die von Dritten betrieben werden.
                </p>
              </div>

              <div className="bg-[#1A2B42] rounded-xl p-6">
                <h3 className="text-[#00D9C8] font-medium mb-3">Kontakt bei Fragen</h3>
                <p className="text-[#A0AEC0] leading-relaxed">
                  Sollten Sie Fragen oder Beschwerden haben, kontaktieren Sie xchannels.ai bitte zuerst 
                  online (<a href="mailto:hello@xchannels.ai" className="text-[#00D9C8] hover:underline">hello@xchannels.ai</a>). 
                  Sie können uns auch auf dem Postweg erreichen: xchannels i. Gr., c/o INiTS, 
                  Maria-Jacobi-Gasse 1, 1030 Wien, Österreich.
                </p>
              </div>

              <div className="bg-[#1A2B42] rounded-xl p-6">
                <h3 className="text-[#00D9C8] font-medium mb-3">Haftung für Links</h3>
                <p className="text-[#A0AEC0] leading-relaxed">
                  Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen 
                  Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. 
                  Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber 
                  der Seiten verantwortlich.
                </p>
              </div>
            </div>
          </section>

          {/* Footer Links */}
          <div className="pt-8 border-t border-[#1A2B42] flex flex-wrap gap-4 text-sm">
            <Link to="/datenschutz" className="text-[#A0AEC0] hover:text-[#00D9C8]">
              {lang === 'de' ? 'Datenschutzerklärung' : 'Privacy Policy'}
            </Link>
            <span className="text-[#5A6C7D]">|</span>
            <Link to="/login" className="text-[#A0AEC0] hover:text-[#00D9C8]">
              {lang === 'de' ? 'Anmelden' : 'Sign in'}
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Impressum;
